import {
  AdminAssignInternalUserRole,
  AdminCreateInternalRole,
  AdminFetchInternalRole,
  AdminFetchInternalRoles,
  AdminFetchInternalUsers,
  AdminSetInternalUserStatus,
  AdminUpdateInternalRole,
} from 'src/constants/actionTypes'
import type {
  FetchInternalRolePayload,
  InternalAccessRole,
  InternalAccessUser,
} from 'src/interfaces/internalAccess'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { InternalAccessState } from './types'

const getInitialState = (): InternalAccessState => {
  return {
    users: [],
    usersLoading: false,
    usersError: null,
    roles: [],
    rolesLoading: false,
    rolesError: null,
    roleDetail: null,
    roleDetailLoading: false,
    roleDetailError: null,
    userSaving: false,
    roleSaving: false,
  }
}

const replaceUser = (
  users: InternalAccessUser[],
  updated: InternalAccessUser,
): InternalAccessUser[] => {
  return users.map((user) => {
    return user.id === updated.id ? updated : user
  })
}

const upsertRole = (
  roles: InternalAccessRole[],
  saved: InternalAccessRole,
): InternalAccessRole[] => {
  const exists = roles.some((role) => {
    return role.id === saved.id
  })

  if (!exists) {
    return [...roles, saved]
  }

  return roles.map((role) => {
    return role.id === saved.id ? saved : role
  })
}

// The users list carries `roleName` denormalised, so a rename is patched in place rather than
// refetching every user. Returns the same array when nothing changes.
const renameRoleOnUsers = (
  users: InternalAccessUser[],
  role: InternalAccessRole,
): InternalAccessUser[] => {
  const hasStaleName = users.some((user) => {
    return user.roleId === role.id && user.roleName !== role.name
  })

  if (!hasStaleName) {
    return users
  }

  return users.map((user) => {
    if (user.roleId !== role.id) {
      return user
    }

    return { ...user, roleName: role.name }
  })
}

export const internalAccessReducer = (
  state: InternalAccessState = getInitialState(),
  action: Action<unknown>,
): InternalAccessState => {
  switch (action.type) {
    // Rows are kept on refetch: blanking the table after every save reads as data loss.
    case AdminFetchInternalUsers.type.REQUEST:
      return { ...state, usersLoading: true, usersError: null }
    case AdminFetchInternalUsers.type.SUCCESS:
      return {
        ...state,
        users: (action.payload as InternalAccessUser[] | undefined) ?? [],
        usersLoading: false,
        usersError: null,
      }
    case AdminFetchInternalUsers.type.FAILED:
      return {
        ...state,
        usersLoading: false,
        usersError: resolveErrorMessage(action.payload, 'Failed to load users'),
      }

    case AdminAssignInternalUserRole.type.REQUEST:
    case AdminSetInternalUserStatus.type.REQUEST:
      return { ...state, userSaving: true }
    case AdminAssignInternalUserRole.type.SUCCESS:
    case AdminSetInternalUserStatus.type.SUCCESS: {
      const updated = action.payload as InternalAccessUser | undefined

      if (!updated) {
        return { ...state, userSaving: false }
      }

      return { ...state, userSaving: false, users: replaceUser(state.users, updated) }
    }
    case AdminAssignInternalUserRole.type.FAILED:
    case AdminSetInternalUserStatus.type.FAILED:
      return { ...state, userSaving: false }

    case AdminFetchInternalRoles.type.REQUEST:
      return { ...state, rolesLoading: true, rolesError: null }
    case AdminFetchInternalRoles.type.SUCCESS:
      return {
        ...state,
        roles: (action.payload as InternalAccessRole[] | undefined) ?? [],
        rolesLoading: false,
        rolesError: null,
      }
    case AdminFetchInternalRoles.type.FAILED:
      return {
        ...state,
        rolesLoading: false,
        rolesError: resolveErrorMessage(action.payload, 'Failed to load roles'),
      }

    // A refetch of the role already shown keeps it, so the editor does not flash a skeleton.
    case AdminFetchInternalRole.type.REQUEST: {
      const roleId = (action.payload as FetchInternalRolePayload | undefined)?.roleId
      const isSameRole = Boolean(roleId) && state.roleDetail?.id === roleId

      return {
        ...state,
        roleDetail: isSameRole ? state.roleDetail : null,
        roleDetailLoading: true,
        roleDetailError: null,
      }
    }
    case AdminFetchInternalRole.type.SUCCESS:
      return {
        ...state,
        roleDetail: (action.payload as InternalAccessRole | undefined) ?? null,
        roleDetailLoading: false,
        roleDetailError: null,
      }
    case AdminFetchInternalRole.type.FAILED:
      return {
        ...state,
        roleDetailLoading: false,
        roleDetailError: resolveErrorMessage(action.payload, 'Failed to load the role'),
      }

    case AdminCreateInternalRole.type.REQUEST:
    case AdminUpdateInternalRole.type.REQUEST:
      return { ...state, roleSaving: true }
    case AdminCreateInternalRole.type.SUCCESS:
    case AdminUpdateInternalRole.type.SUCCESS: {
      const saved = action.payload as InternalAccessRole | undefined

      if (!saved) {
        return { ...state, roleSaving: false }
      }

      return {
        ...state,
        roleSaving: false,
        roles: upsertRole(state.roles, saved),
        roleDetail: saved,
        users: renameRoleOnUsers(state.users, saved),
      }
    }
    case AdminCreateInternalRole.type.FAILED:
    case AdminUpdateInternalRole.type.FAILED:
      return { ...state, roleSaving: false }

    default:
      return state
  }
}
